import { Alert, Box, Button, Grid, Paper, Stack, Typography } from '@mui/material'
import { DescriptionOutlined, LanguageOutlined, OpenInNewOutlined, PlayArrowOutlined, TableChartOutlined, VisibilityOutlined } from '@mui/icons-material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import { ICrpFigures, ICrpReport } from '../../../../shared/services/crp-report/crp-report.model'

const DOWNLOADED_FILES_PATH = '/settings/company-settings/downloaded-files'

const ARTEFACTS = [
  { key: 'crpPdf', label: 'Carbon Reduction Plan (PDF)', description: 'The Annex A document to submit and publish.', icon: <DescriptionOutlined /> },
  { key: 'crpHtml', label: 'Web page (HTML)', description: 'A self-contained page you can publish on your own website.', icon: <LanguageOutlined /> },
  { key: 'crpXlsx', label: 'Emissions data (Excel)', description: 'The figures behind the plan, by scope and category.', icon: <TableChartOutlined /> },
]

/**
 * Step 5, generation. Pressing Generate freezes the figures and queues all three artefacts through
 * the standard download queue, so they also appear under Settings → Downloaded Files. Viewing the
 * finished plan and managing its public link happen on the plan's detail page, reached via
 * onViewPlan.
 */
export const CrpStepGenerate: FunctionComponent<{
  report: ICrpReport
  figures: ICrpFigures
  isGenerating: boolean
  onGenerate: () => void
  onViewPlan: () => void
}> = ({ report, figures, isGenerating, onGenerate, onViewPlan }) => {
  const isGenerated = report.status === 'GENERATED'
  const missingSignOff = !report.signOffName

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Generate your plan</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Generating freezes the figures shown in the previous steps and produces the three files below. The
        figures will not change afterwards, even if the underlying emissions are edited, so what you publish
        stays fixed. You can regenerate at any time to pick up newer data.
      </Typography>

      {missingSignOff && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          No sign-off name has been entered. PPN 006 requires board or director-level approval, so add one in
          the previous step before publishing.
        </Alert>
      )}

      {figures.missingRequiredCategories.baseline.length > 0 || figures.missingRequiredCategories.current.length > 0
        ? (
          <Alert severity='warning' sx={{ mb: 3 }}>
            Your plan will be published with one or more required Scope 3 categories flagged as
            &ldquo;Not reported&rdquo;.
          </Alert>
        )
        : null}

      {/* Each grid item is the flex container and its Paper stretches into it. Do NOT put
          `height: '100%'` on the Paper: that percentage cannot resolve while the row's intrinsic
          height is still being computed, so the row gets sized without the card's real content and
          the card overflows its own grid item — which is what made the Generate button below sit on
          top of these cards. Same trap SelectableCard documents for CardContent. */}
      <Box>
        <Grid container spacing={2}>
          {ARTEFACTS.map((artefact) => (
            <Grid item xs={12} md={4} key={artefact.key} sx={{ display: 'flex' }}>
              <Paper variant='outlined' sx={{ p: 2.5, flexGrow: 1 }}>
                <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                  <Box sx={{ color: 'text.secondary' }}>{artefact.icon}</Box>
                  <Box>
                    <Typography variant='subtitle2'>{artefact.label}</Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {artefact.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant='contained'
          startIcon={<PlayArrowOutlined sx={{ fontSize: 'small' }} />}
          onClick={onGenerate}
          disabled={isGenerating}
          sx={{ textTransform: 'none' }}
        >
          <Typography sx={{ pl: 0.5 }}>
            {isGenerating ? 'Generating…' : (isGenerated ? 'Regenerate plan' : 'Generate plan')}
          </Typography>
        </Button>
      </Box>

      {isGenerated && (
        <Box sx={{ mt: 2 }}>
          {report.generatedAt && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Last generated {moment(report.generatedAt).format('DD/MM/YYYY [at] HH:mm')}. Your files are in
              Settings → Downloaded Files. View the plan to read it as published and to manage its public
              link.
            </Typography>
          )}
          <Stack direction='row' flexWrap='wrap' useFlexGap spacing={1}>
            <Button
              variant='outlined'
              startIcon={<VisibilityOutlined sx={{ fontSize: 'small' }} />}
              onClick={onViewPlan}
              sx={{ textTransform: 'none' }}
            >
              <Typography sx={{ pl: 0.5 }}>View plan</Typography>
            </Button>
            {/* Opens in a new tab so a part-completed plan is not lost by navigating away. */}
            <Button
              variant='outlined'
              component='a'
              href={DOWNLOADED_FILES_PATH}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<OpenInNewOutlined sx={{ fontSize: 'small' }} />}
              sx={{ textTransform: 'none' }}
            >
              <Typography sx={{ pl: 0.5 }}>View downloaded files</Typography>
            </Button>
          </Stack>
        </Box>
      )}

      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 4 }}>
        {figures.disclaimerText}
      </Typography>
    </>
  )
}
